import { ReportHandler } from 'web-vitals';

// Performance metrics storage
interface PerformanceMetrics {
  componentRenderTimes: Record<string, number[]>;
  apiCallTimes: Record<string, number[]>;
  longTasks: { name: string; duration: number; timestamp: number }[];
  errors: { message: string; location: string; timestamp: number }[];
  resourceLoadTimes: { name: string; duration: number; timestamp: number }[];
  memorySnapshots: { usedJSHeapSize: number; totalJSHeapSize: number; timestamp: number }[];
  navigationEvents: { from: string; to: string; duration: number; timestamp: number }[];
}

// Initialize metrics storage
const metrics: PerformanceMetrics = {
  componentRenderTimes: {},
  apiCallTimes: {},
  longTasks: [],
  errors: [],
  resourceLoadTimes: [],
  memorySnapshots: [],
  navigationEvents: []
};

// Function to get all collected metrics
export const getCollectedMetrics = (): PerformanceMetrics => {
  return { ...metrics };
};

// Initialize performance monitoring
export const initPerformanceMonitoring = (onPerfEntry?: ReportHandler) => {
  if (onPerfEntry && typeof onPerfEntry === 'function') {
    import('web-vitals').then(({ onCLS, onFID, onFCP, onLCP, onTTFB }) => {
      onCLS(onPerfEntry);
      onFID(onPerfEntry);
      onFCP(onPerfEntry);
      onLCP(onPerfEntry);
      onTTFB(onPerfEntry);
    });
  }
  
  // Monitor long tasks
  if ('PerformanceObserver' in window) {
    try {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          // Only log tasks longer than 50ms
          if (entry.duration > 50) {
            metrics.longTasks.push({
              name: 'Long Task',
              duration: entry.duration,
              timestamp: performance.now()
            });
            console.warn(`[Performance] Long task detected: ${entry.duration.toFixed(2)}ms`);
          }
        });
      });
      
      observer.observe({ entryTypes: ['longtask'] });
    } catch (e) {
      console.warn('PerformanceObserver for longtask not supported', e);
    }
  }
  
  // Monitor resource loading
  if ('PerformanceObserver' in window) {
    try {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.initiatorType === 'fetch' || entry.initiatorType === 'xmlhttprequest') {
            metrics.resourceLoadTimes.push({
              name: entry.name,
              duration: entry.duration,
              timestamp: performance.now()
            });
            
            // Log slow network requests (> 2s)
            if (entry.duration > 2000) {
              console.warn(`[Performance] Slow network request: ${entry.name} took ${entry.duration.toFixed(2)}ms`);
            }
          }
        });
      });
      
      observer.observe({ entryTypes: ['resource'] });
    } catch (e) {
      console.warn('PerformanceObserver for resource not supported', e);
    }
  }
  
  // Monitor navigation events
  window.addEventListener('popstate', () => {
    metrics.navigationEvents.push({
      from: document.referrer,
      to: window.location.href,
      duration: 0, // We don't know the duration yet
      timestamp: performance.now()
    });
  });
  
  // Monitor errors
  window.addEventListener('error', (event) => {
    metrics.errors.push({
      message: event.message,
      location: event.filename,
      timestamp: performance.now()
    });
    
    console.error('[Global Error]', {
      message: event.message,
      location: event.filename,
      lineNumber: event.lineno,
      columnNumber: event.colno,
      timestamp: new Date().toISOString(),
      url: window.location.href
    });
  });
  
  // Monitor unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const errorMessage = event.reason?.message || 'Unknown promise rejection';
    
    metrics.errors.push({
      message: errorMessage,
      location: 'Promise Rejection',
      timestamp: performance.now()
    });
    
    console.error('[Unhandled Promise Rejection]', {
      message: errorMessage,
      reason: event.reason,
      timestamp: new Date().toISOString(),
      url: window.location.href
    });
  });
  
  // Take memory snapshots periodically
  if ('memory' in performance) {
    setInterval(() => {
      const memory = (performance as any).memory;
      metrics.memorySnapshots.push({
        usedJSHeapSize: memory.usedJSHeapSize,
        totalJSHeapSize: memory.totalJSHeapSize,
        timestamp: performance.now()
      });
    }, 30000); // Every 30 seconds
  }
};

// Track component render time
export const trackRenderTime = (componentName: string) => {
  const startTime = performance.now();
  
  return () => {
    const endTime = performance.now();
    const renderTime = endTime - startTime;
    
    // Store the render time
    if (!metrics.componentRenderTimes[componentName]) {
      metrics.componentRenderTimes[componentName] = [];
    }
    metrics.componentRenderTimes[componentName].push(renderTime);
    
    console.log(`[Performance] ${componentName} rendered in ${renderTime.toFixed(2)}ms`);
    
    // Log to performance monitoring service if render time is high
    if (renderTime > 100) {
      console.warn(`[Performance Warning] ${componentName} took ${renderTime.toFixed(2)}ms to render`);
    }
    
    return renderTime;
  };
};

// Track API call time
export const trackApiCall = async <T>(
  apiCallName: string, 
  apiCall: () => Promise<T>
): Promise<T> => {
  const startTime = performance.now();
  
  try {
    const result = await apiCall();
    const endTime = performance.now();
    const callTime = endTime - startTime;
    
    // Store the API call time
    if (!metrics.apiCallTimes[apiCallName]) {
      metrics.apiCallTimes[apiCallName] = [];
    }
    metrics.apiCallTimes[apiCallName].push(callTime);
    
    console.log(`[API Performance] ${apiCallName} completed in ${callTime.toFixed(2)}ms`);
    
    // Log to performance monitoring service if API call is slow
    if (callTime > 1000) {
      console.warn(`[API Performance Warning] ${apiCallName} took ${callTime.toFixed(2)}ms`);
    }
    
    return result;
  } catch (error) {
    const endTime = performance.now();
    const callTime = endTime - startTime;
    
    console.error(`[API Performance Error] ${apiCallName} failed after ${callTime.toFixed(2)}ms`, error);
    throw error;
  }
};

// Track memory usage
export const trackMemoryUsage = () => {
  if ('memory' in performance) {
    const memory = (performance as any).memory;
    
    // Add to memory snapshots
    metrics.memorySnapshots.push({
      usedJSHeapSize: memory.usedJSHeapSize,
      totalJSHeapSize: memory.totalJSHeapSize,
      timestamp: performance.now()
    });
    
    console.log(`[Memory] Used JS Heap: ${(memory.usedJSHeapSize / 1048576).toFixed(2)} MB`);
    console.log(`[Memory] Total JS Heap: ${(memory.totalJSHeapSize / 1048576).toFixed(2)} MB`);
    console.log(`[Memory] JS Heap Limit: ${(memory.jsHeapSizeLimit / 1048576).toFixed(2)} MB`);
    
    return {
      usedJSHeapSize: memory.usedJSHeapSize,
      totalJSHeapSize: memory.totalJSHeapSize,
      jsHeapSizeLimit: memory.jsHeapSizeLimit
    };
  }
  
  return null;
};

// Network connection monitoring
export const monitorNetworkConnection = (callback?: (online: boolean) => void) => {
  const handleOnline = () => {
    console.log('[Network] Connection restored');
    callback?.(true);
  };
  
  const handleOffline = () => {
    console.log('[Network] Connection lost');
    callback?.(false);
  };
  
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
};

// Track Supabase connection status
export const trackSupabaseConnection = (supabaseClient: any) => {
  let lastStatus = 'unknown';
  
  const checkConnection = async () => {
    try {
      const { data, error } = await supabaseClient.from('databases').select('id').limit(1);
      
      if (error) {
        if (lastStatus !== 'error') {
          console.error('[Supabase] Connection error:', error.message);
          lastStatus = 'error';
        }
        return false;
      }
      
      if (lastStatus !== 'connected') {
        console.log('[Supabase] Connection successful');
        lastStatus = 'connected';
      }
      
      return true;
    } catch (error) {
      if (lastStatus !== 'error') {
        console.error('[Supabase] Connection error:', error);
        lastStatus = 'error';
      }
      return false;
    }
  };
  
  // Initial check
  checkConnection();
  
  // Set up interval to check connection
  const intervalId = setInterval(checkConnection, 30000); // Check every 30 seconds
  
  return () => {
    clearInterval(intervalId);
  };
};

// Track component mount/unmount lifecycle
export const trackComponentLifecycle = (componentName: string) => {
  console.log(`[Lifecycle] ${componentName} mounted`);
  const mountTime = performance.now();
  
  return () => {
    const unmountTime = performance.now();
    const lifetimeDuration = unmountTime - mountTime;
    console.log(`[Lifecycle] ${componentName} unmounted after ${lifetimeDuration.toFixed(2)}ms`);
  };
};

// Get performance summary
export const getPerformanceSummary = () => {
  const summary = {
    slowestComponents: [] as {name: string, avgTime: number}[],
    slowestApiCalls: [] as {name: string, avgTime: number}[],
    memoryTrend: 'stable' as 'increasing' | 'stable' | 'decreasing',
    errorCount: metrics.errors.length,
    longTaskCount: metrics.longTasks.length,
    navigationCount: metrics.navigationEvents.length
  };
  
  // Calculate average render times for components
  const componentAvgTimes = Object.entries(metrics.componentRenderTimes).map(([name, times]) => {
    const avgTime = times.reduce((sum, time) => sum + time, 0) / times.length;
    return { name, avgTime };
  }).sort((a, b) => b.avgTime - a.avgTime);
  
  summary.slowestComponents = componentAvgTimes.slice(0, 5);
  
  // Calculate average API call times
  const apiAvgTimes = Object.entries(metrics.apiCallTimes).map(([name, times]) => {
    const avgTime = times.reduce((sum, time) => sum + time, 0) / times.length;
    return { name, avgTime };
  }).sort((a, b) => b.avgTime - a.avgTime);
  
  summary.slowestApiCalls = apiAvgTimes.slice(0, 5);
  
  // Analyze memory trend
  if (metrics.memorySnapshots.length > 5) {
    const recentSnapshots = metrics.memorySnapshots.slice(-5);
    const firstUsage = recentSnapshots[0].usedJSHeapSize;
    const lastUsage = recentSnapshots[recentSnapshots.length - 1].usedJSHeapSize;
    
    if (lastUsage > firstUsage * 1.2) { // 20% increase
      summary.memoryTrend = 'increasing';
    } else if (lastUsage < firstUsage * 0.8) { // 20% decrease
      summary.memoryTrend = 'decreasing';
    } else {
      summary.memoryTrend = 'stable';
    }
  }
  
  return summary;
};